import { Injectable } from '@angular/core';
import { wallArtProductTypeUids } from '@api-ui-app/src/app/e-commerce-product-expansion/types/wall-art-product-type-uids';
import { SubscriptionsHelperService } from '@api-ui-app/src/app/subscriptions/services/subscriptions-helper.service';
import { DesignEditorPlugin } from '@gelato-api-ui/core/design-editor/types/design-editor-plugin.enum';
import {
  PluginDataState,
  SetPluginsEventDataItem,
} from '@gelato-api-ui/core/design-editor/types/set-plugins-event-data-item';
import { ProductTypeUid } from '@gelato-api-ui/core/product-catalogue/product-type-uid.enum';
import { ApparelProductTypeUids, BagProductTypeUids } from '@gelato-api-ui/core/product-catalogue/product-type-uids';
import { ApparelAndAccessoriesSanityProductCategoryNames } from '@gelato-api-ui/core/sanity/constants/sanity-categories/apparel-and-accessories-sanity-product-category-names';
import { SanityProductCategoryName } from '@gelato-api-ui/core/sanity/constants/sanity-categories/sanity-product-category-name.enum';
import { DesignEditorMode } from '@gelato-api-ui/design-editor/types/design-editor-mode.enum';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class EditorPluginService {
  private imageProviderProductTypeAvailable: ProductTypeUid[] = [
    ...wallArtProductTypeUids,
    ProductTypeUid.MUG,
    ProductTypeUid.BOTTLE,
    ProductTypeUid.WALLPAPER,
    ProductTypeUid.WALLPAPER_SAMPLE,
    ProductTypeUid.PHONE_CASE,
    ...ApparelProductTypeUids,
    ...BagProductTypeUids,
  ];

  private imageProviderNotWallpaperProductTypeAvailable: ProductTypeUid[] =
    this.imageProviderProductTypeAvailable.filter(
      type => type !== ProductTypeUid.WALLPAPER && type !== ProductTypeUid.WALLPAPER_SAMPLE,
    );

  private imageProviderCategoryTypeAvailable = new Set([
    SanityProductCategoryName.POSTERS,
    SanityProductCategoryName.FRAMED_POSTERS,
    SanityProductCategoryName.POSTERS_WITH_HANGERS,
    SanityProductCategoryName.CANVAS,
    SanityProductCategoryName.ACRYLIC,
    SanityProductCategoryName.METALLIC,
    SanityProductCategoryName.FOAM_BOARD_PRINT,
    SanityProductCategoryName.WOOD_PRINT,
    SanityProductCategoryName.MUGS,
    SanityProductCategoryName.PHONE_CASES,
    ...ApparelAndAccessoriesSanityProductCategoryNames,
  ]);

  private pluginProductTypeAvailability = {
    [DesignEditorPlugin.APP_SHUTTERSTOCK]: this.imageProviderProductTypeAvailable,
    [DesignEditorPlugin.APP_IMAGE_FILTERS]: Object.values(ProductTypeUid).filter(
      type => type !== ProductTypeUid.WALLPAPER && type !== ProductTypeUid.WALLPAPER_SAMPLE,
    ),
    [DesignEditorPlugin.APP_ADVANCED_PERSONALIZATION]: this.imageProviderNotWallpaperProductTypeAvailable,
  };

  constructor(
    private readonly translateService: TranslateService,
    private readonly subscriptionsHelperService: SubscriptionsHelperService,
  ) {}

  getImageEnhancementPlugin(productTypeUid: ProductTypeUid): SetPluginsEventDataItem {
    return {
      name: DesignEditorPlugin.APP_IMAGE_ENHANCEMENT,
      state: this.getPluginState(productTypeUid, DesignEditorPlugin.APP_IMAGE_ENHANCEMENT),
      type: 'image-manipulation',
      icon: `${window.location.origin}/assets/editor/image-enhancement.svg`,
      title: this.translateService.instant('txt_editor_plugin_image_enhancement'),
    };
  }

  getImageEnhancementPluginExtra(): SetPluginsEventDataItem['extra'] {
    return {
      modifyImageLabel: this.translateService.instant('txt_editor_plugin_image_enhancement'),
      resetImageLabel: this.translateService.instant('txt_editor_plugin_restore_enhancement'),
      previewModeInfo: this.translateService.instant('txt_editor_plugin_image_enhancement_description'),
      acceptModificationLabel: this.translateService.instant('txt_editor_plugin_done'),
      declineModificationLabel: this.translateService.instant('txt_editor_plugin_cancel'),
    };
  }

  getBackgroundRemovalPlugin(productTypeUid: ProductTypeUid): SetPluginsEventDataItem {
    return {
      name: DesignEditorPlugin.APP_BACKGROUND_REMOVAL,
      state: this.getPluginState(productTypeUid, DesignEditorPlugin.APP_BACKGROUND_REMOVAL),
      type: 'image-manipulation',
      icon: `${window.location.origin}/assets/editor/bg-removal-icon.svg`,
      title: this.translateService.instant('txt_editor_plugin_background_removal'),
    };
  }

  getAIPersonalizationPlugin(): SetPluginsEventDataItem {
    return {
      name: DesignEditorPlugin.APP_AI_PERSONALIZATION,
      state: PluginDataState.enabled,
    };
  }

  getShutterstockPlugin(): SetPluginsEventDataItem {
    return {
      name: DesignEditorPlugin.APP_SHUTTERSTOCK,
      indexInTabs: 5,
      state: PluginDataState.enabled,
    };
  }

  getBackgroundRemovalPluginExtra(): SetPluginsEventDataItem['extra'] {
    return {
      modifyImageLabel: this.translateService.instant('txt_editor_plugin_remove_background'),
      resetImageLabel: this.translateService.instant('txt_editor_plugin_restore_ackground'),
      previewModeInfo: this.translateService.instant('txt_editor_plugin_background_description'),
      acceptModificationLabel: this.translateService.instant('txt_editor_plugin_done'),
      declineModificationLabel: this.translateService.instant('txt_editor_plugin_cancel'),
    };
  }

  getPluginByAppName(appName: DesignEditorPlugin, productTypeUid: ProductTypeUid): SetPluginsEventDataItem {
    const plugins = {
      [DesignEditorPlugin.APP_BACKGROUND_REMOVAL]: this.getBackgroundRemovalPlugin(productTypeUid),
      [DesignEditorPlugin.APP_SHUTTERSTOCK]: this.getShutterstockPlugin(),
    };

    return plugins[appName] || {};
  }

  getPluginExtraByAppName(appName: DesignEditorPlugin): any {
    const plugins = {
      [DesignEditorPlugin.APP_BACKGROUND_REMOVAL]: this.getBackgroundRemovalPluginExtra(),
    };

    return plugins[appName] || {};
  }

  isPluginAvailableByProduct(appName: DesignEditorPlugin, productTypeUid: ProductTypeUid): boolean {
    if (!this.pluginProductTypeAvailability[appName] || !productTypeUid) {
      return true;
    }
    return !!this.pluginProductTypeAvailability[appName].includes(productTypeUid);
  }

  isPluginAvailableByMode(appName: DesignEditorPlugin, mode: DesignEditorMode): boolean {
    if (mode === DesignEditorMode.PRODUCT_EXPANSION_PRODUCT_EDITING) {
      return ![
        DesignEditorPlugin.APP_SHUTTERSTOCK,
        DesignEditorPlugin.APP_GRAPHICS,
        DesignEditorPlugin.APP_ADVANCED_PERSONALIZATION,
      ].includes(appName);
    }

    return true;
  }

  isShutterstockAvailableByProduct(productTypeUid: ProductTypeUid): boolean {
    return this.isPluginAvailableByProduct(DesignEditorPlugin.APP_SHUTTERSTOCK, productTypeUid);
  }

  isShutterstockTabSupported(sanityProductCategoryName: string): boolean {
    return this.imageProviderCategoryTypeAvailable.has(sanityProductCategoryName as SanityProductCategoryName);
  }

  getPluginPromptData(
    isGelatoPlusPromptShown: boolean,
    isGelatoPlusOrGoldActive: boolean,
    isUserNeverHadGelatoPlusSubscription: boolean,
    designEditorPlugin: DesignEditorPlugin,
  ) {
    return isGelatoPlusPromptShown
      ? {
          description: this.subscriptionsHelperService.getGelatoPlusPromptDescription(
            isGelatoPlusOrGoldActive,
            designEditorPlugin,
          ),
          buttonText: isUserNeverHadGelatoPlusSubscription
            ? this.translateService.instant('txt_start_free_trial')
            : this.translateService.instant('txt_subscribe_now'),
        }
      : null;
  }

  private getPluginState(productTypeUid: ProductTypeUid, pluginName: DesignEditorPlugin): PluginDataState {
    let pluginState = PluginDataState.not_visible;
    if (this.isPluginAvailableByProduct(pluginName, productTypeUid)) {
      pluginState = PluginDataState.enabled;
    }

    return pluginState;
  }
}
